import { Autocomplete as MuiAutocomplete, IconButton, TextField } from '@mui/material';
import { search_icon, x_lightgray, x_gray400 } from '@assets/icons';
import { colorSet } from '@theme';
import { CSSProperties, useEffect, useRef } from 'react';
import { Text } from '..';

interface Props {
  label?: string;
  placeholder?: string;
  tags: string[];
  inputValue: string;
  options: string[];
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onInputChange: (event: React.SyntheticEvent, newValue: string) => void;
  onAutocompleteChange: (event: React.SyntheticEvent, value: string | null) => void;
  onTagClear: (tag: string) => void;
  onClear: () => void;
  onSubmit: () => void;
  boxstyle?: CSSProperties;
}

// 피그마 'Search' 컴포넌트 + 오토컴플리트 기능 제공 + SearchBar 내부에 생성된 태그 보여주기 기능 제공
export const TagSearchBar = (props: Props) => {
  const {
    label,
    placeholder,
    tags,
    inputValue,
    options,
    onKeyDown,
    onInputChange,
    onAutocompleteChange,
    onTagClear,
    onClear,
    onSubmit,
    boxstyle
  } = props;

  const tagContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tagContainerRef.current) {
      tagContainerRef.current.scrollLeft = tagContainerRef.current.scrollWidth;
    }
  }, [tags]);

  return (
    <div style={boxstyle}>
      {label ? (
        <Text variant="subtitle" fontWeight="bold" marginBottom="8px">
          {label}
        </Text>
      ) : null}

      <MuiAutocomplete
        className="search-bar-autocomplete" // theme.ts MuiAutocomplete style 적용을 위한 className 부여
        freeSolo
        open={options && options.length > 0}
        inputValue={inputValue}
        options={options}
        onKeyDown={onKeyDown}
        onInputChange={(event, newValue) => {
          onInputChange(event, newValue);
        }}
        value={null} //inputValue와 options 요소가 일치할 시 onChange가 실행되지 않는 이슈 픽스
        onChange={(event, newValue, reason) => {
          //options 리스트에서 키워드를 선택했을 시에만 실행
          if (reason === 'selectOption' && newValue && options.includes(newValue)) {
            onAutocompleteChange(event, newValue);
          }
        }}
        isOptionEqualToValue={(option, value) => option === value} //inputValue와 options 요소가 일치할 시 onChange가 실행되지 않는 이슈 픽스
        renderInput={(params) => (
          <TextField
            {...params}
            //InputProps는 v7에서 지원종료 예정, 그러나 slotProps 적용시 options가 보이지 않는 이슈 있어 일단 유지
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <div
                  ref={tagContainerRef}
                  style={{
                    display: 'flex',
                    overflowX: 'auto',
                    maxWidth: '400px',
                    whiteSpace: 'nowrap',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  }}
                >
                  {tags.map((tag) => (
                    <div
                      key={tag}
                      style={{
                        height: 28,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        padding: '0px 10px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginRight: '10px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Text variant="body1" marginRight="2px">
                        {tag}
                      </Text>
                      <img
                        src={x_gray400}
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                        onClick={() => onTagClear(tag)}
                      />
                    </div>
                  ))}
                </div>
              ),
              endAdornment: (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {(tags.length > 0 || inputValue) && (
                    <>
                      <IconButton onClick={onClear}>
                        <img src={x_lightgray} style={{ width: 20, height: 20 }} />
                      </IconButton>
                      <div
                        style={{
                          height: 32,
                          borderRight: `0.5px solid ${colorSet.gray300}`,
                          marginLeft: 10,
                          marginRight: 12
                        }}
                      />
                    </>
                  )}
                  <IconButton onClick={onSubmit}>
                    <img src={search_icon} style={{ width: 20, height: 20 }} />
                  </IconButton>
                </div>
              )
            }}
            placeholder={tags.length > 0 ? '' : (placeholder ?? 'Search')}
          />
        )}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 1]
                }
              }
            ],
            sx: {
              width: 'calc(100% - 44px)' // 부모 컨테이너보다 44px 작게
            }
          },
          paper: {
            sx: {
              width: 'calc(100% - 44px)', // 부모 컨테이너보다 44px 작게
              borderRadius: 0,
              boxShadow: `0px 5px 25px rgba(0, 0, 0, 0.1)`
            }
          },
          listbox: {
            sx: {
              width: '100%'
            }
          }
        }}
        sx={{
          width: '100%', // 부모 컨테이너의 너비에 맞춤
          '& .MuiAutocomplete-inputRoot': {
            width: '100%' // 입력 필드는 부모 컨테이너의 전체 너비 사용
          }
        }}
      />
    </div>
  );
};
